/**
 * "Fetching latest information: 12 of 28 sources…" (R147, R168; docs/specs/space-weather.md §4.4): the banner under
 * the Space weather title while a round of answers is coming in, with a bar that fills as each source answers (the
 * page asks them one at a time, so the count is real), gone when the round ends. Not red: red is the QC fail colour
 * and never means "busy"; the banner is the accent's soft tint (`accent-soft`) with the bar in `accent`.
 *
 * Screen readers: the words are one `role="status"` live region, announced when the round starts and again, as
 * "Fetching finished.", when it ends (the region stays in the page, visually hidden, so the end is heard); the count
 * is `aria-hidden` and the bar a `progressbar` with its value, so no source is announced on its own. "Finished" comes
 * from the hook's count of ended rounds, not from this component having seen the round start: answers that are
 * already on the page's storage come back faster than React draws.
 */
import type { ReactElement } from 'react';
import type { SwProgress } from './useSpaceWeatherData.ts';

export function FetchingBanner({
  progress,
  rounds,
}: {
  readonly progress: SwProgress | null;
  /** Rounds that have ended since the page opened (`SpaceWeatherData.rounds`). */
  readonly rounds: number;
}): ReactElement {
  const pct = progress && progress.total > 0 ? Math.round((100 * progress.answered) / progress.total) : 0;
  return (
    <div className={progress ? 'notice notice--fetching sw-fetching' : 'sr-only'}>
      <p className="body sw-fetching__words" role="status" aria-live="polite">
        {progress ? (
          <>
            Fetching latest information
            <span className="sw-fetching__count" aria-hidden="true">
              : {progress.answered} of {progress.total} sources
            </span>
            … The figures below update as each source answers.
          </>
        ) : rounds > 0 ? (
          'Fetching finished.'
        ) : (
          ''
        )}
      </p>
      {progress && (
        <div
          className="sw-fetching__bar"
          role="progressbar"
          aria-label="Sources answered"
          aria-valuemin={0}
          aria-valuemax={progress.total}
          aria-valuenow={progress.answered}
        >
          <span className="sw-fetching__fill" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}
